import type { getOrganisationConfigurationList_getOrganisationConfigurationList } from 'Constants/graphql/queries/organisation/__generated__/getOrganisationConfigurationList'
import { SupportedLanguagesEnum } from 'Constants/locale'
import {
  ORGANISATION_CONFIG_LIST_ENABLED_LANGUAGES_ATTRIBUTE_VALUE_TRUE,
  ORGANISATION_CONFIG_LIST_ENABLED_LANGUAGES_NAME,
} from 'Constants/organisationConfig'

const ALL_LANGUAGES: ReadonlySet<SupportedLanguagesEnum> = new Set([
  SupportedLanguagesEnum.DE,
  SupportedLanguagesEnum.EN,
  SupportedLanguagesEnum.FR,
])

const ATTRIBUTE_NAME_TO_LANGUAGE: Record<string, SupportedLanguagesEnum> = {
  de: SupportedLanguagesEnum.DE,
  en: SupportedLanguagesEnum.EN,
  fr: SupportedLanguagesEnum.FR,
}

/**
 * Derives the set of portal languages enabled for the current organisation from
 * its raw configuration list. When the organisation has no explicit
 * "enabled_languages" config saved, or has it saved with every language
 * disabled, we treat that as "no restriction" and return all three languages —
 * matching the backend's lazy default and avoiding lockouts when an admin saves
 * an all-disabled config.
 */
export const deriveEnabledPortalLanguages = (
  configList:
    | getOrganisationConfigurationList_getOrganisationConfigurationList[]
    | null,
): ReadonlySet<SupportedLanguagesEnum> => {
  if (!configList) {
    return ALL_LANGUAGES
  }

  const enabledLanguagesConfig = configList.find(
    config =>
      config?.name === ORGANISATION_CONFIG_LIST_ENABLED_LANGUAGES_NAME,
  )

  if (!enabledLanguagesConfig) {
    return ALL_LANGUAGES
  }

  const enabled = new Set<SupportedLanguagesEnum>()

  for (const attribute of enabledLanguagesConfig.attributes) {
    const language = ATTRIBUTE_NAME_TO_LANGUAGE[attribute.attributeName]

    if (
      language &&
      attribute.attributeValue ===
        ORGANISATION_CONFIG_LIST_ENABLED_LANGUAGES_ATTRIBUTE_VALUE_TRUE
    ) {
      enabled.add(language)
    }
  }

  if (enabled.size === 0) {
    return ALL_LANGUAGES
  }

  return enabled
}
